"use client"

import { type ReactNode, useLayoutEffect, useState } from "react"

import { useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"

import { HeaderSearch } from "@/components/marketplace/layout/header-search"
import { scrollPastTopBar } from "@/components/marketplace/layout/top-bar-offset"
import { Wordmark } from "@/components/marketplace/layout/wordmark"
import { Row } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { usePathname } from "@/i18n/navigation"
import { useRecentSearches } from "@/lib/hooks/search/use-recent-searches"
import { useLocalizedPush } from "@/lib/hooks/use-navigation"
import { buildRoute, ROUTES, segmentsAfterRegion } from "@/lib/routes"
import { SEARCH_PARAM_KEYS } from "@/lib/types/search"
import { useInNativeHost } from "@/platform/bridge/use-native-bridge"
import { createConversionId } from "@/platform/observability/analytics/conversions"
import { trackSearch } from "@/platform/observability/analytics/v2/search"

export const MobileNav = ({ account }: { account?: ReactNode }) => {
  const t = useTranslations("nav")
  const pathname = usePathname()
  const onProductPage = segmentsAfterRegion(pathname)[0] === ROUTES.PRODUCTS
  const inNativeHost = useInNativeHost()
  useLayoutEffect(() => {
    if (onProductPage) queueMicrotask(scrollPastTopBar)
  }, [pathname, onProductPage])
  const urlQuery = useSearchParams().get(SEARCH_PARAM_KEYS.Q) ?? ""
  const [searchText, setSearchText] = useState(urlQuery)
  const [prevUrlQuery, setPrevUrlQuery] = useState(urlQuery)
  if (prevUrlQuery !== urlQuery) {
    setPrevUrlQuery(urlQuery)
    setSearchText(urlQuery)
  }
  const { add: addRecent } = useRecentSearches()
  const localizedPush = useLocalizedPush()
  const goToResults = (value: string = searchText) => {
    const term = value.trim()
    if (term.length > 0) {
      trackSearch({ search_term: term, conversionId: createConversionId() })
      addRecent(term)
    }
    localizedPush(buildRoute.search({ text: term }))
  }

  return (
    <div data-bare-top={onProductPage ? "" : undefined} className={`bg-surface @container ${onProductPage && inNativeHost ? "max-md:hidden" : ""}`}>
      <div className="px-gut pt-375 pb-375 md:pt-400 md:pb-300">
        <Row justify="between" gap="250" className="h-8.75 items-start md:h-11 md:items-center md:gap-500">
          <LocalizedClientLink
            href="/"
            aria-label={t("brandHome")}
            className="press focus-visible:ring-brand hit-target inline-flex shrink-0 items-center rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            <Wordmark tone="brand" height={30} />
          </LocalizedClientLink>
          <div className="hidden max-w-130 min-w-0 flex-1 md:block">
            <HeaderSearch
              tone="line"
              size="md"
              submit
              glyph={false}
              placeholder={t("searchPlaceholder")}
              className="w-full min-w-0"
              panelClassName="z-dropdown absolute top-full right-0 left-0 mt-300 overflow-hidden"
              value={searchText}
              onValueChange={setSearchText}
              onSubmitTerm={goToResults}
            />
          </div>
          {!onProductPage && (account ?? <span aria-hidden className="inline-block h-8.75 w-24 shrink-0 md:h-11 md:w-29" />)}
        </Row>
      </div>
    </div>
  )
}
